
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/datepicker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { saveBabyProfile, getBabyProfile } from "@/utils/babyProfile";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useAuth } from "@/contexts/auth";

export function BabyProfileForm({ onComplete }: { onComplete?: () => void }) {
  const [babyName, setBabyName] = useState("");
  const [babyBirthday, setBabyBirthday] = useState<Date | undefined>(undefined);
  const [originalHeight, setOriginalHeight] = useState("");
  const [originalWeight, setOriginalWeight] = useState("");
  const [gender, setGender] = useState<'male' | 'female' | 'other' | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    const loadProfile = async () => {
      const profile = await getBabyProfile();
      if (profile) {
        setBabyName(profile.name || "");
        setBabyBirthday(profile.birthday ? new Date(profile.birthday) : undefined);
        setOriginalHeight(profile.originalHeight || "");
        setOriginalWeight(profile.originalWeight || "");
        setGender(profile.gender);
      }
    };
    
    loadProfile();
  }, []);

  const handleSave = async () => {
    if (!babyName) {
      toast.error(language === 'ja' ? "赤ちゃんの名前を入力してください" : "Please enter your baby's name");
      return;
    }
    
    setLoading(true);
    
    try {
      await saveBabyProfile({
        name: babyName,
        birthday: babyBirthday ? babyBirthday.toISOString() : undefined,
        originalHeight,
        originalWeight,
        gender
      });

      toast.success(language === 'ja' ? "赤ちゃんのプロフィールを保存しました" : "Baby profile saved successfully!");
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Error saving baby profile:", error);
      toast.error(language === 'ja' ? "保存中にエラーが発生しました" : "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>{t('baby.profile', language)}</CardTitle>
        <CardDescription>
          {language === 'ja' 
            ? "日記エントリー全体で使用する赤ちゃんの詳細を入力してください" 
            : "Enter your baby's details to use across all diary entries"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="babyName">{t('baby.name', language)}</Label>
          <Input 
            id="babyName" 
            value={babyName} 
            onChange={(e) => setBabyName(e.target.value)} 
            placeholder={language === 'ja' ? "赤ちゃんの名前を入力" : "Enter your baby's name"}
          />
        </div>
        
        <div className="space-y-2">
          <Label>{t('baby.birthday', language)}</Label>
          <DatePicker 
            date={babyBirthday} 
            onDateChange={setBabyBirthday}
          />
        </div>
        
        <div className="space-y-2">
          <Label>{t('baby.gender', language)}</Label>
          <RadioGroup value={gender} onValueChange={(value: 'male' | 'female' | 'other') => setGender(value)} className="flex space-x-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="male" id="male" />
              <Label htmlFor="male">{t('baby.gender.male', language)}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="female" id="female" />
              <Label htmlFor="female">{t('baby.gender.female', language)}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="other" id="other" />
              <Label htmlFor="other">{t('baby.gender.other', language)}</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="originalHeight">{t('baby.originalHeight', language)}</Label>
          <Input 
            id="originalHeight" 
            value={originalHeight} 
            onChange={(e) => setOriginalHeight(e.target.value)} 
            placeholder={language === 'ja' ? "例: 50 cm" : "e.g. 50 cm"}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="originalWeight">{t('baby.originalWeight', language)}</Label>
          <Input 
            id="originalWeight" 
            value={originalWeight} 
            onChange={(e) => setOriginalWeight(e.target.value)} 
            placeholder={language === 'ja' ? "例: 3500 g" : "e.g. 3500 g"}
          />
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleSave} 
          className="w-full"
          disabled={loading}
        >
          {loading 
            ? (language === 'ja' ? "保存中..." : "Saving...") 
            : t('common.save', language)}
        </Button>
      </CardFooter>
    </Card>
  );
}
